import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { reserveOrCheckBuffer } from './reserveOrCheckBuffer';

import { clearBuffer, setBufferReadyToApply } from '../actions/buffer';
import { setBufferLocationId } from '../reducers/buffer/locationId';
import { setBufferSaleType } from '../reducers/buffer/saleType';
import { $getLocationId } from '../selectors/getLocationId';
import { getOrderingProvider } from '../selectors/config';
import { DELIVERY_SALE_TYPES, SALE_TYPE } from '../constants/saleType';
import { FAILURE_REASON } from '../constants';
import { applyLocationEstimate } from './applyLocationEstimate';
import {
  getBufferDeliveryTime,
  getLocation,
  getLocationDeliveryEstimates,
  getMember,
  getPickupTime,
  getEnableDynamicPaymentGatewayConfig,
} from '../selectors';
import { fetchOffers } from './fetchOffers';
import { applyBuffer } from './applyBuffer';
import { fetchMenu } from './fetchMenu';
import { fetchOrderingProviderDetails } from './fetchOrderingProviderDetails';
import { fetchDeliveryEstimate } from './fetchDeliveryEstimate';
import { fetchPaymentGatewayTokens } from './fetchPaymentGatewayTokens';
import { fetchPaymentGatewayConfig } from './fetchPaymentGatewayConfig';
import { setBypassDeliveryDetails } from '../operations';
import Logger from '../utils/Logger';
import moment from 'moment';
import { ORDERING_PROVIDER } from '../constants/orderingProvider';
import { IDeliveryDropOffCoordinatesHttpDto } from '../dtos/interfaces';
import { isEmpty } from 'lodash';

export interface UpdateKeyOrderProperyDto {
  locationId?: string;
  saleType?: SALE_TYPE;
  autoApply?: boolean;
  desiredDeliveryTime?: string;
  deliveryAddress?: string;
  updateDeliveryEstimate?: boolean;
  confirmLocationDeliveryEstimate?: boolean;
  forceASAPDeliveryEstimate?: boolean;
  bypassDeliveryDetails?: boolean;
  isASAP?: boolean;
  readonly deliveryDropOffCoordinates?: IDeliveryDropOffCoordinatesHttpDto;
}
export const updateKeyOrderProperty = createAsyncThunk(
  '$updateKeyOrderProperty',
  async (
    data: UpdateKeyOrderProperyDto,
    { dispatch, getState, rejectWithValue },
  ) => {
    const {
      locationId,
      saleType,
      autoApply,
      desiredDeliveryTime,
      deliveryAddress,
      updateDeliveryEstimate,
      confirmLocationDeliveryEstimate,
      forceASAPDeliveryEstimate,
      bypassDeliveryDetails,
      isASAP,
      deliveryDropOffCoordinates,
    } = data;

    let controlsBuffer;
    let reason = FAILURE_REASON.UNKNOWN;
    let userReason: string | undefined;
    let systemReason: string | undefined;
    let receivedMultipleEstimates = false;

    const isDelivery = saleType && DELIVERY_SALE_TYPES.includes(saleType);

    try {
      const flowId = uuidv4();
      dispatch(reserveOrCheckBuffer({ flowId }));

      controlsBuffer = true;

      // This was moved from the bottom of the file to here
      // so we could get the initial buffer location id.
      // Ideally this needs to be a function for better unit testability
      const performDeliveryEstimate =
        !confirmLocationDeliveryEstimate &&
        (isDelivery || updateDeliveryEstimate) &&
        (!bypassDeliveryDetails || deliveryAddress);

      const enableDynamicPaymentGatewayConfig =
        getEnableDynamicPaymentGatewayConfig(getState() as EntireFrontendState);

      const orderingProvider = getOrderingProvider(
        getState() as EntireFrontendState,
      );

      const initialBufferLocationId = $getLocationId(
        getState() as EntireFrontendState,
      );

      const isNotKiosk = orderingProvider !== ORDERING_PROVIDER.KIOSK;
      if (isNotKiosk) {
        dispatch(fetchPaymentGatewayTokens({}));
      }

      if (!isDelivery && locationId != null) {
        dispatch(setBypassDeliveryDetails(false));
      }

      if (saleType != null) {
        const location = getLocation(getState() as EntireFrontendState);
        if (
          location &&
          !location.availableSaleTypesToday[saleType].isAvailable &&
          !isDelivery
        ) {
          throw new Error(
            `Saletype ${saleType} is not available now for location ${location.name}`,
          );
        }

        Logger.log('Setting buffer saletype : ' + saleType);
        dispatch(setBufferSaleType(saleType));
      }

      if (!performDeliveryEstimate && locationId != null) {
        dispatch(setBufferLocationId(locationId));
      }
      // performing delivery estimate
      if (performDeliveryEstimate) {
        if (locationId != null && !bypassDeliveryDetails) {
          throw new Error('cannot provide locationId with delivery saleType');
        }

        let fetchParams: any = { forceASAPDeliveryEstimate };

        if (updateDeliveryEstimate) {
          fetchParams = {
            ...fetchParams,
            locationId: initialBufferLocationId,
          };
        } else {
          fetchParams = {
            ...fetchParams,
            deliveryAddress,
            desiredDeliveryTime,
            multiple: true,
            locationId: bypassDeliveryDetails ? locationId : undefined,
            saleType: saleType,
          };
        }

        if (deliveryAddress) {
          const { multiple } = await dispatch(
            fetchDeliveryEstimate({
              ...fetchParams,
              ...(!isEmpty(deliveryDropOffCoordinates)
                ? { deliveryDropOffCoordinates }
                : {}),
            }),
          ).unwrap();

          console.log({ multiple });

          if (multiple) {
            receivedMultipleEstimates = true;
          }
        }
      }

      //confirm delivery estimate
      if (confirmLocationDeliveryEstimate) {
        if (locationId === null) {
          throw new Error('missing locationId');
        }

        const locationEstimates = getLocationDeliveryEstimates(
          getState() as EntireFrontendState,
        );
        const estimate = locationEstimates.find(
          locationEstimate => locationEstimate.locationId === locationId,
        );

        if (!estimate) {
          throw new Error('no matching estimate for locationId');
        }

        // Apply the location estimate
        dispatch(
          applyLocationEstimate({
            ...estimate,
            bestDeliveryTime: estimate.bestDeliveryTime,
            isASAP,
          }),
        );
      }

      if (!receivedMultipleEstimates) {
        const bufferLocationId = $getLocationId(
          getState() as EntireFrontendState,
        );

        if (bufferLocationId == null) {
          return;
        }

        if (enableDynamicPaymentGatewayConfig) {
          dispatch(
            fetchPaymentGatewayConfig({
              locationId: bufferLocationId,
            }),
          );
        }

        const bufferLocationIdChanged =
          bufferLocationId !== initialBufferLocationId;

        // menu and ordering provider details read/write disjoint state slices,
        // so they can be fetched concurrently. Offers are fire-and-forget —
        // nothing before applyBuffer needs them to have resolved.

        const menuPromise =
          bufferLocationIdChanged || !updateDeliveryEstimate
            ? dispatch(fetchMenu()).unwrap()
            : Promise.resolve();

        if (bufferLocationIdChanged) {
          const memberPresent = Boolean(
            getMember(getState() as EntireFrontendState),
          );

          if (memberPresent) {
            if (isNotKiosk) {
              dispatch(fetchOffers({ bufferMode: true }));
            } else {
              // Fetch offers should not be fetched here for Kiosk, because this
              // same call will be fetched when you scan member coupon / barcode.
              // For Kiosk android, if trusted calls are made asynchronously, all
              // following calls using the trusted token will fail.
              await dispatch(fetchOffers({ bufferMode: true }));
            }
          }
        }

        let orderingProviderDetailsPromise: Promise<unknown> =
          Promise.resolve();
        if (!performDeliveryEstimate || !updateDeliveryEstimate) {
          const pickupDateTime = getPickupTime(
            getState() as EntireFrontendState,
          );

          // Delivery time is not set from buffer in fetchOrderingProviderDetails at this point
          const bufferDeliveryDateTime = getBufferDeliveryTime(
            getState() as EntireFrontendState,
          );

          let dateTime;
          if (isDelivery && bufferDeliveryDateTime !== 'ASAP') {
            dateTime = bufferDeliveryDateTime;
          } else {
            dateTime = pickupDateTime;
          }

          //fetch ordering provider details
          if (saleType) {
            orderingProviderDetailsPromise = dispatch(
              fetchOrderingProviderDetails({
                bufferMode: true,
                date:
                  dateTime && dateTime !== 'ASAP'
                    ? moment(dateTime).format('l')
                    : undefined,
              }),
            ).unwrap();
          }
        }

        await Promise.all([menuPromise, orderingProviderDetailsPromise]);
      }

      dispatch(setBufferReadyToApply(!receivedMultipleEstimates));

      if (
        !receivedMultipleEstimates &&
        (autoApply || updateDeliveryEstimate || confirmLocationDeliveryEstimate)
      ) {
        await dispatch(applyBuffer()).unwrap();
      }
    } catch (e) {
      if (controlsBuffer) {
        dispatch(clearBuffer({}));
      }

      console.warn('Update key order property failed', e, {
        reason,
        userReason,
        systemReason,
      });
      return rejectWithValue({
        //@ts-ignore
        ...e,
        reason: (e as any).reason ? (e as any).reason : reason,
        userReason: (e as any).userReason ? (e as any).userReason : userReason,
        systemReason: (e as any).systemReason
          ? (e as any).systemReason
          : systemReason,
      });
    }
  },
);
